import { supabase } from '../supabase';
import { EXCLUDE_REFERENTI } from './contactPollution';

/**
 * Auto-associazione persona→azienda per dominio email (stile HubSpot).
 * Estrae il dominio dall'email della persona e cerca un'azienda dell'org con
 * lo stesso dominio (da website o email). Usato per SUGGERIRE il collegamento
 * (non automatico e cieco: l'utente conferma).
 * Vedi MD/architecture/contatti-aziende-firstclass-migrazione.md
 */

// Domini pubblici/free: non sono domini aziendali → nessun match.
const PUBLIC_DOMAINS = new Set([
  'gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'hotmail.it', 'live.com', 'live.it',
  'msn.com', 'yahoo.com', 'yahoo.it', 'icloud.com', 'me.com', 'mac.com', 'aol.com', 'proton.me',
  'protonmail.com', 'gmx.com', 'gmx.it',
  // Italiani comuni
  'libero.it', 'virgilio.it', 'alice.it', 'tin.it', 'tiscali.it', 'fastwebnet.it', 'email.it',
  'inwind.it', 'iol.it', 'tim.it', 'poste.it', 'pec.it', 'aruba.it', 'pecaziendale.it',
]);

/** Estrae il dominio da un'email; null se assente, malformato o dominio pubblico. */
export function extractCompanyDomain(email: string | null | undefined): string | null {
  if (!email) return null;
  const at = email.trim().toLowerCase().split('@');
  if (at.length !== 2 || !at[1]) return null;
  const domain = at[1].replace(/^www\./, '');
  if (!domain.includes('.') || PUBLIC_DOMAINS.has(domain)) return null;
  return domain;
}

export interface DomainCompanyMatch {
  id: string;
  business_name: string;
}

/**
 * Trova un'azienda dell'org con lo stesso dominio (da website o email).
 * Ritorna il primo match, o null. Esclude le persone-referente (Fase 2).
 */
export async function findCompanyByDomain(
  organizationId: string,
  domain: string,
): Promise<DomainCompanyMatch | null> {
  if (!organizationId || !domain) return null;
  try {
    const { data, error } = await supabase
      .from('clients')
      .select('id, business_name, website, email')
      .eq('organization_id', organizationId)
      .eq('entity_type', 'company')
      .or(`website.ilike.%${domain}%,email.ilike.%@${domain}%`)
      .or(EXCLUDE_REFERENTI)
      .limit(5);
    if (error || !data) return null;
    // preferisci un match "pulito": dominio esatto nell'email o nel website
    const best = data.find((c: any) => {
      const w = (c.website || '').toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '');
      const e = (c.email || '').toLowerCase();
      return w.startsWith(domain) || w.includes('/' + domain) || w === domain || e.endsWith('@' + domain);
    }) || data[0];
    return best ? { id: best.id, business_name: best.business_name } : null;
  } catch {
    return null;
  }
}
